import { useState, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';

/**
 * Custom hook for converting markdown text to renderable HTML
 * @param {string} markdownText - The markdown text to convert
 * @param {object} options - Configuration options for the markdown rendering
 * @returns {object} - Object containing the rendered HTML and utility functions
 */
const useMarkdown = (markdownText = '', options = {}) => {
  const [renderedContent, setRenderedContent] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Default options
  const defaultOptions = {
    // Custom components for rendering
    components: {},
    // Custom styling
    className: '',
    // Allow HTML in markdown (for v8 compatibility)
    allowDangerousHtml: false,
    // Remark plugins for markdown processing
    remarkPlugins: [],
  };

  // Memoize options to prevent unnecessary re-renders
  const memoizedOptions = useMemo(() => ({
    allowDangerousHtml: options.allowDangerousHtml ?? defaultOptions.allowDangerousHtml,
    className: options.className ?? defaultOptions.className,
    components: options.components ?? defaultOptions.components,
    remarkPlugins: options.remarkPlugins ?? defaultOptions.remarkPlugins,
  }), [
    options.allowDangerousHtml, 
    options.className, 
    // Only depend on the reference of components and remarkPlugins
    options.components, 
    options.remarkPlugins
  ]);

  useEffect(() => {
    if (!markdownText) {
      setRenderedContent(null);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Create the markdown component with the provided options
      const markdownComponent = (
        <ReactMarkdown
          allowDangerousHtml={memoizedOptions.allowDangerousHtml}
          components={memoizedOptions.components}
          className={memoizedOptions.className}
          remarkPlugins={memoizedOptions.remarkPlugins}
        >
          {markdownText}
        </ReactMarkdown>
      );

      setRenderedContent(markdownComponent);
    } catch (err) {
      setError(err.message || 'Failed to render markdown');
      setRenderedContent(null);
    } finally {
      setIsLoading(false);
    }
  }, [markdownText, memoizedOptions]);

  // Utility function to get plain text from markdown
  const getPlainText = useMemo(() => {
    return (text) => {
      if (!text) return '';
      // Simple regex to remove markdown syntax
      return text
        .replace(/#{1,6}\s+/g, '') // Remove headers
        .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold
        .replace(/\*(.*?)\*/g, '$1') // Remove italic
        .replace(/`(.*?)`/g, '$1') // Remove inline code
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Remove links
        .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1') // Remove images
        .replace(/^\s*[-*+]\s+/gm, '') // Remove list markers
        .replace(/^\s*\d+\.\s+/gm, '') // Remove numbered list markers
        .replace(/^\s*>/gm, '') // Remove blockquotes
        .replace(/^\s*`{3,}.*$/gm, '') // Remove code blocks
        .replace(/\n{3,}/g, '\n\n') // Normalize line breaks
        .trim();
    };
  }, []);

  // Utility function to check if text contains markdown
  const hasMarkdown = useMemo(() => {
    return (text) => {
      if (!text) return false;
      const markdownPatterns = [
        /#{1,6}\s/, // Headers
        /\*\*.*?\*\*/, // Bold
        /\*.*?\*/, // Italic
        /`.*?`/, // Inline code
        /\[.*?\]\(.*?\)/, // Links
        /!\[.*?\]\(.*?\)/, // Images
        /^\s*[-*+]\s+/m, // Lists
        /^\s*\d+\.\s+/m, // Numbered lists
        /^\s*>/m, // Blockquotes
        /^\s*`{3,}/m, // Code blocks
      ];
      return markdownPatterns.some(pattern => pattern.test(text));
    };
  }, []);

  // Utility function to extract links from markdown
  const extractLinks = useMemo(() => {
    return (text) => {
      if (!text) return [];
      const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
      const links = [];
      let match;
      while ((match = linkRegex.exec(text)) !== null) {
        links.push({
          text: match[1],
          url: match[2],
        });
      }
      return links;
    };
  }, []);

  return {
    // The rendered React component
    renderedContent,
    // Loading state
    isLoading,
    // Error state
    error,
    // Utility functions
    getPlainText,
    hasMarkdown,
    extractLinks,
    // Original markdown text
    markdownText,
    // Options used
    options: memoizedOptions,
  };
};

export default useMarkdown; 